// src/i18next.js
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const apiKey = 'd58973b4c5922bd93ddfba1d4dc78ee8c9f4a5da1501fb209177fa8d5065cd26'; 
const serverPort = 'https://embedded.translatory.tech';

/**
 * Injects translatory.js if not already loaded.
 */
const injectTranslatoryScript = () => {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[data-api-key="${apiKey}"][src*="translatory.js"]`);
    if (existingScript) {
   
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `${serverPort}/public/translatory.js`;
    script.setAttribute('data-api-key', apiKey);
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load translatory.js'));

    document.head.appendChild(script);
  });
};

/**
 * Waits for window.translatory to be fully initialized with languages and translations.
 */
const waitForTranslatory = () => {
  return new Promise((resolve, reject) => {
    const timeoutLimit = 10000; // 10 seconds
    const startTime = Date.now();

    const checkTranslatory = () => {
      if (
        window.translatory &&
        window.translatory.translations &&
        Object.keys(window.translatory.translations).length > 0 &&
        window.translatory.availableLanguages &&
        window.translatory.availableLanguages.length > 0
      ) {
        resolve(window.translatory);
      } else if (Date.now() - startTime > timeoutLimit) {
        reject(new Error('Timed out waiting for Translatory to load.'));
      } else {
        setTimeout(checkTranslatory, 100);
      }
    };

    checkTranslatory();
  });
};

/**
 * Injects translatoryedit.js if not already loaded.
 * This is optional and separate, so call this function whenever you need the edit functionality.
 */
const injectTranslatoryEditScript = () => {
  return new Promise((resolve, reject) => {
    // Check if translatoryedit.js with our apiKey is already present
    const existingScript = document.querySelector(`script[data-api-key="${apiKey}"][src*="translatoryedit.js"]`);
    if (existingScript) {
      // Already loaded
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `${serverPort}/public/translatoryedit.js`;
    script.setAttribute('data-api-key', apiKey);
    script.async = true;

    script.onload = () => {
      // Once loaded, translatoryedit.js should run its own logic
      resolve();
    };

    script.onerror = () => reject(new Error('Failed to load translatoryedit.js'));

    document.head.appendChild(script);
  });
};

const initializeI18n = async () => {
  try {
    // 1. Inject translatory.js if needed
    await injectTranslatoryScript();

    // 2. Wait for translatory to be ready
    const translatory = await waitForTranslatory();

    // Determine default language
    const savedLanguage = localStorage.getItem('selectedLanguage');
    const browserLanguage = navigator.language || navigator.userLanguage;
    const defaultLanguage =
      savedLanguage || translatory.currentLanguage || browserLanguage || 'en';

    translatory.currentLanguage = defaultLanguage;

    // Prepare resources for i18next
    const resources = {
      [translatory.currentLanguage]: {
        translation: { ...translatory.translations },
      },
    };

    // Initialize i18n
    await i18n.use(initReactI18next).init({
      interpolation: { escapeValue: false },
      resources,
      lng: defaultLanguage,
      fallbackLng: 'en',
      ns: ['translation'],
      defaultNS: 'translation',
      initImmediate: false,
    });

    // Store the chosen language
    localStorage.setItem('selectedLanguage', translatory.currentLanguage);

    // Add a method for dynamic updates
    translatory.updateTranslations = (newTranslations, newLanguage) => {
      i18n.addResourceBundle(newLanguage, 'translation', newTranslations, true, true);
      i18n.changeLanguage(newLanguage);
      localStorage.setItem('selectedLanguage', newLanguage);
    };

    // Track active translation keys
    window.activeTranslationKeys = new Set();

    const originalT = i18n.t.bind(i18n);
    i18n.t = (...args) => {
      const key = args[0];
      if (key) window.activeTranslationKeys.add(key);
      return originalT(...args);
    };

    window.logActiveTranslationKeys = () => {
      console.log('Active Translation Keys:', Array.from(window.activeTranslationKeys));
    };

    window.resetActiveTranslationKeys = () => {
      window.activeTranslationKeys.clear();
    };

  } catch (error) {
    console.error('Error initializing i18n:', error);
  }
};

/**
 * If you want to load translatoryedit.js (optional), call this function after translations are ready.
 * Example usage:
 * translatory(() => {
 *   // i18n initialized
 *   injectTranslatoryEditScript().then(() => {
 *     // translatoryedit.js loaded and ready
 *   });
 * });
 */

export const loadTranslatoryEdit = () => {
  return injectTranslatoryEditScript();
};

export const translatory = (callback) => {
  initializeI18n()
    .then(() => {
      callback();
    })
    .catch((err) => {
      console.error('Error initializing i18n:', err);
    });
};

export default i18n;